import React, { useEffect, useState } from 'react';
import { useAppDispatch } from '../../store/hooks';
import { createRecipe, importRecipeFromUrl } from '../../store/recipesSlice';
import { fetchIngredients } from '../../store/ingredientsSlice';
import useCsrfToken from '../../hooks/useCsrfToken';
import { useNavigate } from 'react-router-dom';
import RecipeForm from './RecipeForm';
import { RecipeFormData } from './RecipeForm';

const EMPTY_FORM: RecipeFormData = {
  name: '',
  description: '',
  instructions: '',
  ingredients_attributes: [],
  visibility: 'private',
};

const NewRecipeForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const csrfToken = useCsrfToken();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<RecipeFormData>(EMPTY_FORM);
  const [importUrl, setImportUrl] = useState('');
  const [importStatus, setImportStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [importError, setImportError] = useState<string | null>(null);

  useEffect(() => {
    setFormData(EMPTY_FORM);
    dispatch(fetchIngredients({ perPage: 9999 }));
  }, [dispatch]);

  const handleImport = () => {
    if (!importUrl.trim()) return;
    setImportStatus('loading');
    setImportError(null);
    dispatch(importRecipeFromUrl({ url: importUrl, csrfToken }))
      .unwrap()
      .then((data) => { setFormData(data); setImportStatus('idle'); })
      .catch((err: Error) => { setImportStatus('error'); setImportError(err.message); });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(createRecipe({
      data: formData,
      csrfToken
    }))
    .unwrap()
    .then(() => {
      navigate('/recipes/mine');
    })
    .catch((error: Error) => {
      alert('Failed to create recipe: ' + error.message);
    });
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-stone-100 mb-6">New Recipe</h2>

      <div className="mb-6 p-4 bg-navy-900 border border-army-800 rounded-lg">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
          Import from URL
        </p>
        <div className="flex gap-2">
          <input
            type="url"
            value={importUrl}
            onChange={(e) => setImportUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleImport()}
            placeholder="https://www.allrecipes.com/recipe/..."
            className="flex-1 px-3 py-2 bg-army-950 border border-army-800 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-neon transition-colors"
          />
          <button
            type="button"
            onClick={handleImport}
            disabled={importStatus === 'loading' || !importUrl.trim()}
            className="px-4 py-2 bg-army-800 hover:bg-army-700 text-white border border-army-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 whitespace-nowrap"
          >
            {importStatus === 'loading' ? 'Importing…' : 'Import'}
          </button>
        </div>
        {importStatus === 'error' && importError && (
          <p className="mt-2 text-sm text-rose-400">{importError}</p>
        )}
      </div>

      <RecipeForm
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        submitButtonText="Create Recipe"
      />
    </div>
  );
};

export default NewRecipeForm;
